import { z } from 'zod'

const FeatureStateSchema = z
  .object({
    enabled: z.boolean().default(false),
    defaultValue: z.boolean().default(false),
  })
  .default({
    enabled: false,
    defaultValue: false,
  })

export const FeaturesSchema = z.object({
  advertiserIndustryMatch: FeatureStateSchema,
  allowAllAdvertisers: FeatureStateSchema,
  allowCpmDiscount: FeatureStateSchema,
  deleteOrders: FeatureStateSchema,
  flightDates: FeatureStateSchema,
  flightplan: FeatureStateSchema,
  selectMcp: FeatureStateSchema,
  testFeature: FeatureStateSchema,
})

export type Features = z.infer<typeof FeaturesSchema>

export type FeatureRequirements = Partial<Record<keyof Features, boolean>>

export type RequiredFeatures<Requirements extends FeatureRequirements> = {
  [Feature in keyof Requirements]-?: Feature extends keyof Features
    ? Features[Feature] & { enabled: Requirements[Feature] }
    : never
}

export type FeatureConfig = Record<
  string,
  {
    defaultValue: boolean
    emails: string[]
    userIds: number[]
    advertiserIds: number[]
  }
>

export const featureKeys = FeaturesSchema.keyof().options

const emailMatches = (emails: string[], emailAddress: string) =>
  emails.some(email => email.trim().toLowerCase() === emailAddress.trim().toLowerCase())

export const resolveFeatures = ({
  features,
  userId,
  emailAddress,
  advertiserIds = [],
}: {
  features: FeatureConfig
  userId: number
  emailAddress: string
  advertiserIds?: number[]
}): Features => {
  return FeaturesSchema.parse(
    Object.fromEntries(
      featureKeys.map(feature => {
        const config = features[feature]
        if (!config) {
          return [feature, { enabled: false, defaultValue: false }]
        }

        const match = isMatch({ config, userId, emailAddress, advertiserIds })
        return [
          feature,
          {
            enabled: match,
            defaultValue: match ? config.defaultValue : false,
          },
        ]
      }),
    ),
  )
}

function isMatch({
  config,
  userId,
  emailAddress,
  advertiserIds,
}: {
  config: FeatureConfig[string]
  userId: number
  emailAddress: string
  advertiserIds: number[]
}) {
  const { emails, userIds, advertiserIds: configAIDs } = config

  const userIdMatch = userIds.includes(userId)
  const emailMatch = emailMatches(emails, emailAddress)
  const advertiserMatch = configAIDs.some(id => advertiserIds.includes(id))

  return userIdMatch || emailMatch || advertiserMatch
}
